import { useCallback, useEffect, useMemo, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import {
  APPEARANCE_STORAGE_KEY,
  applyAppearanceToDocument,
  getStoredAppearanceMode,
  resolveAppearanceMode,
} from '@/theme/appearanceTheme'
import { createAppTheme } from '@/theme/muiTheme'
import AppFluentProvider from './AppFluentProvider.jsx'
import { AppearanceThemeContext } from './AppThemeProvider.part01.jsx'

export default function AppThemeProvider({ children }) {
  const [mode, setModeState] = useState(getStoredAppearanceMode)
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)').matches : false,
  )

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event) => setSystemDark(event.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const resolvedMode = useMemo(() => {
    if (mode === 'system') return systemDark ? 'dark' : 'light'
    return resolveAppearanceMode(mode)
  }, [mode, systemDark])

  useEffect(() => {
    applyAppearanceToDocument(resolvedMode)
  }, [resolvedMode])

  useEffect(() => {
    try {
      window.localStorage.setItem(APPEARANCE_STORAGE_KEY, mode)
    } catch {
      /* ignore */
    }
  }, [mode])

  const setMode = useCallback((nextMode) => {
    setModeState(nextMode)
  }, [])

  const muiTheme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode])

  const value = useMemo(() => ({ mode, setMode, resolvedMode }), [mode, setMode, resolvedMode])

  return (
    <AppearanceThemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <AppFluentProvider mode={resolvedMode}>{children}</AppFluentProvider>
      </ThemeProvider>
    </AppearanceThemeContext.Provider>
  )
}
